import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  Chip,
  Stack,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  alpha,
  useTheme,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrendingUp,
  Zap,
  Crown,
  Star,
  Shield,
  Users,
  Clock,
  Check,
  CreditCard,
  User,
  Mail,
  Phone,
  MapPin,
  Hash,
  AlertCircle,
} from 'lucide-react'
import { Close } from '@mui/icons-material'
import api from '../../api/axiosInstance'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'

const ModernSubscriptionPage = () => {
  const theme = useTheme()
  const [billingPeriod, setBillingPeriod] = useState('monthly')
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [openBillingDialog, setOpenBillingDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [plans, setPlans] = useState([])
  const [currentSubscription, setCurrentSubscription] = useState(null)
  const [userInfo, setUserInfo] = useState(null)

  const [billingInfo, setBillingInfo] = useState({
    name: '',
    surname: '',
    identityNumber: '',
    email: '',
    gsmNumber: '',
    registrationAddress: '',
    city: 'istanbul',
    country: 'turkey',
  })

  const cities = [
    'istanbul',
    'ankara',
    'izmir',
    'bursa',
    'antalya',
    'adana',
    'konya',
    'gaziantep',
    'mersin',
    'kayseri',
  ]

  // Fetch plans from API
  const fetchPlans = async () => {
    try {
      setLoading(true)
      const response = await api.get('/plans/')
      console.log("resposne: ", response)
      setPlans(response.data)
    } catch (error) {
      console.error('Error fetching plans:', error)
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Planlar yüklenirken bir hata oluştu',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } finally {
      setLoading(false)
    }
  }

  // Fetch user info and current subscription
  const fetchUserData = async () => {
    try {
      const [userResponse] = await Promise.all([
        api.get('/auth/me'),
      ])
      console.log("user :", userResponse)
      const subscription = userResponse.data.user.subscriptions.find(s => s.status === 'ACTIVE')
      setUserInfo(userResponse.data.user)
      setCurrentSubscription(subscription)
      setBillingInfo((prev) => ({
        ...prev, name: userResponse.data.user.name || '',
        surname: userResponse.data.user.surname || '',
        identityNumber: '',
        email: userResponse.data.user.email || '',
        gsmNumber: userResponse.data.user.phone || '',
        registrationAddress: userResponse.data.user.address || '',
        city: userResponse.data.user.city?.toLowerCase() || '',
        country: userResponse.data.user.country?.toLowerCase() || '',
      }))
    } catch (error) {
      console.error('Error fetching user data:', error)
    }
  }

  useEffect(() => {
    fetchPlans()
    fetchUserData()
  }, [])

  const getPlanIcon = (planName) => {
    const name = planName.toLowerCase()
    if (name.includes('free') || name.includes('ücretsiz')) return Zap
    if (name.includes('pro') || name.includes('premium')) return Crown
    if (name.includes('enterprise') || name.includes('özel')) return Star
    return Shield
  }

  const getPlanColor = (index) => {
    const colors = ['#4facfe', '#10b981', '#f59e0b', '#8b5cf6']
    return colors[index % colors.length]
  }

  const isCurrentPlan = (planId) => {
    return currentSubscription?.planId === planId
  }

  const handleSelectPlan = (plan) => {
    // Check if it's the current plan
    if (isCurrentPlan(plan.id)) {
      Swal.fire({
        icon: 'info',
        title: 'Bilgi',
        text: 'Bu plan zaten aktif',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return
    }

    // Check if it's a special plan
    if (plan.isSpecial) {
      Swal.fire({
        icon: 'info',
        title: 'Özel Plan',
        text: 'Bu plan için lütfen bizimle iletişime geçin',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return
    }

    // Check if it's free and price is 0
    if (plan.price === 0) {
      handleSubscribe(plan, null)
      return
    }

    setSelectedPlan(plan)
    setOpenBillingDialog(true)
  }

  const handleCloseBillingDialog = () => {
    setOpenBillingDialog(false)
    setSelectedPlan(null)
  }

  const handleBillingInfoChange = (field, value) => {
    setBillingInfo((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateBillingInfo = () => {
    if (!billingInfo.name || !billingInfo.surname) {
      Swal.fire({
        icon: 'error',
        title: 'Eksik Bilgi',
        text: 'Lütfen ad ve soyad bilgilerinizi giriniz',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return false
    }

    if (!billingInfo.email || !/\S+@\S+\.\S+/.test(billingInfo.email)) {
      Swal.fire({
        icon: 'error',
        title: 'Geçersiz Email',
        text: 'Lütfen geçerli bir email adresi giriniz',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return false
    }

    if (!billingInfo.identityNumber || billingInfo.identityNumber.length !== 11) {
      Swal.fire({
        icon: 'error',
        title: 'Geçersiz TC Kimlik No',
        text: 'Lütfen 11 haneli TC Kimlik No giriniz',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return false
    }

    if (!billingInfo.gsmNumber) {
      Swal.fire({
        icon: 'error',
        title: 'Eksik Bilgi',
        text: 'Lütfen telefon numaranızı giriniz',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return false
    }

    if (!billingInfo.registrationAddress) {
      Swal.fire({
        icon: 'error',
        title: 'Eksik Bilgi',
        text: 'Lütfen adres bilginizi giriniz',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
      return false
    }

    return true
  }

  const handleSubscribe = async (plan, billingData) => {
    if (billingData && !validateBillingInfo()) {
      return
    }

    try {
      setLoading(true)

      // Backend planId'yi path'ten alıyor (POST /v1/subscriptions/:planId).
      // Fatura bilgisi şu an sunucuda kullanılmıyor; gövde bilerek boş.
      const response = await api.post(`/subscriptions/${plan.id}`, {})

      handleCloseBillingDialog()

      // Backend, RahatPay ödeme sayfasının tam adresini redirectUrl olarak
      // döndürüyor. Ödeme sayfasını yeni sekmede açıyoruz; abonelik ancak
      // RahatPay webhook'u geldiğinde ACTIVE olacak.
      const redirectUrl = response.data?.redirectUrl
      if (redirectUrl) {
        window.open(redirectUrl, '_blank', 'noopener,noreferrer')
        // Kullanıcı ödeme sekmesine geçtiğinde bu sayfadaki bilgiyi tazeleyelim.
        await fetchUserData()
        return
      }

      // redirectUrl yoksa (ör. ücretsiz plan) ödeme adımı yok — bilgi ver.
      Swal.fire({
        icon: 'info',
        title: 'Ödeme bekleniyor',
        text: 'Abonelik kaydınız oluşturuldu. Ödeme onaylandığında paketiniz aktifleşecek.',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })

      // Refresh data
      await fetchUserData()

    } catch (error) {
      console.error('Subscription error:', error)
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.response?.data?.message || 'Abonelik oluşturulurken bir hata oluştu',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } finally {
      setLoading(false)
    }
  }

  const getPlanPrice = (plan) => {
    if (plan.isSpecial || !plan.price) return 'Özel Fiyat'
    // Convert from kuruş to TL
    const priceInTL = plan.price / 100
    return priceInTL
  }

  const formatFeatures = (features) => {
    if (!features || typeof features !== 'object') return []

    return Object.entries(features)
      .filter(([key, value]) => value === true)
      .map(([key]) => {
        // Convert snake_case to readable format
        return key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      })
  }

  const formatLimits = (limits) => {
    if (!limits || typeof limits !== 'object') return {}
    return limits
  }

  if (loading && plans.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          background: `radial-gradient(ellipse at top, ${alpha(
            theme.palette.primary.main,
            0.05
          )}, transparent 50%)`,
        }}
      />

      {/* Floating particles */}
      {[...Array(3)].map((_, i) => (
        <Box
          key={i}
          component={motion.div}
          animate={{
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 5 + i,
            repeat: Infinity,
            delay: i * 0.5,
          }}
          sx={{
            position: 'fixed',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.main,
              0.1
            )}, transparent)`,
            filter: 'blur(60px)',
            top: `${20 + i * 30}%`,
            left: `${10 + i * 30}%`,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Chip
            label="FİYATLANDIRMA"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              mb: 2,
              fontWeight: 700,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              letterSpacing: '1px',
              fontSize: '0.8rem',
            }}
          />

          <Typography
            variant="h3"
            fontWeight={900}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            İhtiyacınıza Uygun Planı Seçin
          </Typography>

          <Typography
            color={alpha('#fff', 0.7)}
            sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, mb: 4 }}
          >
            Esnek ve ölçeklenebilir izleme çözümleri
          </Typography>

          {/* Current Subscription Alert */}
          {currentSubscription && (
            <Alert
              icon={<Crown size={20} />}
              severity="success"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                mb: 3,
                background: alpha(theme.palette.success.main, 0.1),
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
              }}
            >
              <Typography variant="body2">
                Aktif Paket: <strong>{currentSubscription.plan.name}</strong> -
                Bitiş: <strong>{dayjs(currentSubscription.currentPeriodEnd).format('DD.MM.YYYY')}</strong>
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Plans */}
        <Grid
          container
          spacing={3}
          justifyContent="center"
          maxWidth="1400px"
          sx={{ mx: 'auto' }}
        >
          {plans.map((plan, index) => {
            const Icon = getPlanIcon(plan.name)
            const color = getPlanColor(index)
            const price = getPlanPrice(plan)
            const features = formatFeatures(plan.features)
            const limits = formatLimits(plan.limits)
            const isCurrent = isCurrentPlan(plan.id)

            return (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card
                  component={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: isCurrent ? 1.02 : 1.05 }}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.background.paper,
                      0.8
                    )} 0%, ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
                    backdropFilter: 'blur(20px)',
                    border: isCurrent
                      ? `2px solid ${theme.palette.success.main}`
                      : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transform: isCurrent ? 'scale(1.02)' : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: isCurrent
                        ? `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`
                        : `linear-gradient(90deg, ${alpha(color, 0.5)}, ${alpha(color, 0.2)})`,
                    },
                    '&:hover': {
                      borderColor: isCurrent
                        ? theme.palette.success.main
                        : alpha(theme.palette.primary.main, 0.5),
                      boxShadow: isCurrent
                        ? `0 20px 60px ${alpha(theme.palette.success.main, 0.3)}`
                        : `0 10px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                    },
                  }}
                >
                  {isCurrent && (
                    <Chip
                      icon={<Check size={14} />}
                      label="AKTİF PLAN"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        fontWeight: 800,
                        bgcolor: alpha(theme.palette.success.main, 0.15),
                        color: theme.palette.success.main,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                        zIndex: 1,
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={3}>
                      {/* Icon */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          p: 1.5,
                          bgcolor: alpha(color, 0.1),
                          borderRadius: 2,
                          width: 'fit-content',
                          border: `1px solid ${alpha(color, 0.2)}`,
                          boxShadow: `0 4px 20px ${alpha(color, 0.2)}`,
                        }}
                      >
                        <Icon size={32} color={color} />
                      </Box>

                      {/* Plan Name */}
                      <Box>
                        <Typography
                          variant="h4"
                          fontWeight={900}
                          sx={{
                            color: '#fff',
                            letterSpacing: '0.5px',
                            mb: 0.5,
                          }}
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          color={alpha('#fff', 0.7)}
                          sx={{ fontSize: '0.95rem' }}
                        >
                          {plan.durationDays} günlük paket
                        </Typography>
                      </Box>

                      {/* Price */}
                      <Box>
                        <Typography
                          variant="h3"
                          sx={{
                            color: '#fff',
                            fontWeight: 800,
                            fontSize: '2.5rem',
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                          }}
                        >
                          {typeof price === 'number' ? (
                            <>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '1.5rem',
                                  color: alpha('#fff', 0.6),
                                }}
                              >
                                ₺
                              </Typography>
                              {price}
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '1rem',
                                  color: alpha('#fff', 0.6),
                                  fontWeight: 400,
                                }}
                              >
                                /{plan.durationDays} gün
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '1.5rem',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}
                            >
                              {price}
                            </Typography>
                          )}
                        </Typography>
                      </Box>

                      {/* CTA Button */}
                      <Button
                        variant={isCurrent ? 'outlined' : 'contained'}
                        onClick={() => handleSelectPlan(plan)}
                        disabled={isCurrent}
                        sx={{
                          bgcolor: isCurrent
                            ? 'transparent'
                            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          color: isCurrent ? theme.palette.success.main : '#000',
                          border: isCurrent
                            ? `2px solid ${theme.palette.success.main}`
                            : 'none',
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                          borderRadius: 2,
                          '&:hover': {
                            bgcolor: isCurrent
                              ? alpha(theme.palette.success.main, 0.1)
                              : theme.palette.primary.dark,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 10px 30px ${alpha(
                              isCurrent ? theme.palette.success.main : theme.palette.primary.main,
                              0.3
                            )}`,
                          },
                          '&:disabled': {
                            opacity: 0.7,
                          },
                          transition: 'all 0.3s ease',
                        }}
                        size="large"
                      >
                        {isCurrent
                          ? 'Aktif Plan'
                          : plan.isSpecial
                            ? 'İletişime Geç'
                            : price === 0 || !price
                              ? 'Ücretsiz Başla'
                              : 'Satın Al'}
                      </Button>

                      <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

                      {/* Limits */}
                      <Stack spacing={1.5}>
                        {limits.max_monitors && (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                p: 0.5,
                                bgcolor: alpha(color, 0.1),
                                borderRadius: 1,
                              }}
                            >
                              <Shield size={18} color={color} />
                            </Box>
                            <Typography
                              color={alpha('#fff', 0.9)}
                              sx={{ fontSize: '0.95rem' }}
                            >
                              {limits.max_monitors} Monitor
                            </Typography>
                          </Stack>
                        )}

                        {limits.max_team_members !== undefined && (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                p: 0.5,
                                bgcolor: alpha(color, 0.1),
                                borderRadius: 1,
                              }}
                            >
                              <Users size={18} color={color} />
                            </Box>
                            <Typography
                              color={alpha('#fff', 0.9)}
                              sx={{ fontSize: '0.95rem' }}
                            >
                              {limits.max_team_members === 0
                                ? 'Ekip Yok'
                                : `${limits.max_team_members} Ekip Üyesi`}
                            </Typography>
                          </Stack>
                        )}

                        {limits.min_interval && (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              sx={{
                                p: 0.5,
                                bgcolor: alpha(color, 0.1),
                                borderRadius: 1,
                              }}
                            >
                              <Clock size={18} color={color} />
                            </Box>
                            <Typography
                              color={alpha('#fff', 0.9)}
                              sx={{ fontSize: '0.95rem' }}
                            >
                              {limits.min_interval} dk minimum aralık
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      <Divider sx={{ borderColor: alpha('#fff', 0.1) }} />

                      {/* Features */}
                      <Stack spacing={1.5}>
                        {features.map((feature, idx) => (
                          <Stack
                            key={idx}
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Check
                              size={16}
                              color={color}
                              style={{ flexShrink: 0 }}
                            />
                            <Typography
                              color={alpha('#fff', 0.9)}
                              sx={{ fontSize: '0.9rem' }}
                            >
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Box>

      {/* Billing Info Dialog */}
      <Dialog
        open={openBillingDialog}
        onClose={handleCloseBillingDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <CreditCard size={24} color={theme.palette.primary.main} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Fatura Bilgileri
              </Typography>
              <Typography variant="caption" color={alpha('#fff', 0.6)}>
                {selectedPlan?.name} Planı - ₺{selectedPlan ? getPlanPrice(selectedPlan) : 0}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseBillingDialog}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            {/* Name */}
            <Grid item xs={12} md={6}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Ad *
              </Typography>
              <TextField
                fullWidth
                placeholder="Adınız"
                value={billingInfo.name}
                onChange={(e) => handleBillingInfoChange('name', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User size={18} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Surname */}
            <Grid item xs={12} md={6}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Soyad *
              </Typography>
              <TextField
                fullWidth
                placeholder="Soyadınız"
                value={billingInfo.surname}
                onChange={(e) => handleBillingInfoChange('surname', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User size={18} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Identity Number */}
            <Grid item xs={12} md={6}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                TC Kimlik No *
              </Typography>
              <TextField
                fullWidth
                placeholder="11 haneli TC Kimlik No"
                value={billingInfo.identityNumber}
                onChange={(e) =>
                  handleBillingInfoChange('identityNumber', e.target.value.replace(/\D/g, ''))
                }
                inputProps={{ maxLength: 11 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Hash size={18} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                E-posta *
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="ornek@email.com"
                value={billingInfo.email}
                onChange={(e) => handleBillingInfoChange('email', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={18} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Telefon *
              </Typography>
              <TextField
                fullWidth
                placeholder="+905555555555"
                value={billingInfo.gsmNumber}
                onChange={(e) => handleBillingInfoChange('gsmNumber', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone size={18} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={6}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Şehir *
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={billingInfo.city}
                  onChange={(e) => handleBillingInfoChange('city', e.target.value)}
                  sx={{
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city.charAt(0).toUpperCase() + city.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                Adres *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Kayıt adresi"
                value={billingInfo.registrationAddress}
                onChange={(e) =>
                  handleBillingInfoChange('registrationAddress', e.target.value)
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <MapPin size={18} color={theme.palette.primary.main} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Alert
            icon={<AlertCircle size={20} />}
            severity="info"
            sx={{
              mt: 3,
              background: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Typography variant="body2">
              * ile işaretli alanlar zorunludur. Fatura bilgileriniz güvenli bir şekilde saklanır.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Button
            onClick={handleCloseBillingDialog}
            variant="outlined"
            disabled={loading}
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.5),
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                background: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            İptal
          </Button>
          <Button
            onClick={() => handleSubscribe(selectedPlan, billingInfo)}
            variant="contained"
            disabled={loading}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              px: 4,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              '&:disabled': {
                opacity: 0.6,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Ödemeye Geç'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ModernSubscriptionPage